import React, { useState } from "react";
import dayjs from "dayjs";
import { Alert, Spinner } from "react-bootstrap";
import RIcon from "../reservas/components/ReservasIcons";
import InlineDayPicker from "../reservas/components/InlineDayPicker";
import { SLOT_PERIODS, slotPeriod } from "../beauty/beautyBooking";
import PhoneCountryInput from "../reservas/components/PhoneCountryInput";
import {
  formatDeadline,
  formatDuration,
  formatLongDate,
  formatMoney,
} from "./birthdayModel";
import "./cumpleanos.css";

/*
 * Presentational steps of the birthday flow. They only render what `useBirthdayFlow` hands them:
 * no fetching and no flow rules here.
 */

const DATES_PAGE = 28;

export const PackageSummary = ({ pkg }) => (
  <>
    <div>
      <div className="bday-package-name">{pkg.name}</div>
      <div className="bday-package-meta">{formatDuration(pkg.durationMinutes)}</div>
      {pkg.includes && <div className="bday-package-includes">{pkg.includes}</div>}
    </div>
    <div className="bday-package-price">{formatMoney(pkg.price)}</div>
  </>
);

export function StepPaquete({ packages, selectedId, onSelect }) {
  return (
    <>
      <h2 className="bday-title">Elegí tu paquete</h2>
      <p className="bday-lead">Cada paquete incluye lo que ves abajo. Después elegís la fecha y el horario.</p>
      {packages.length === 0 ? (
        <div className="rmob-empty">
          <RIcon name="info" width={32} height={32} />
          <p>Este club todavía no tiene paquetes de cumpleaños disponibles.</p>
        </div>
      ) : (
        <div className="bday-packages" role="radiogroup" aria-label="Paquetes de cumpleaños">
          {packages.map((pkg) => {
            const selected = String(pkg._id) === String(selectedId);
            return (
              <button
                type="button"
                key={pkg._id}
                role="radio"
                aria-checked={selected}
                className={`bday-package${selected ? " selected" : ""}`}
                onClick={() => onSelect(pkg._id)}
              >
                <PackageSummary pkg={pkg} />
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

export function StepFecha({
  dateOptions,
  date,
  onSelectDate,
  availability,
  slotStart,
  onSelectStart,
  onRetry,
  slotNotice,
  courts,
  courtNameOf,
  calendar = false,
}) {
  const [visible, setVisible] = useState(DATES_PAGE);
  if (calendar) {
    return (
      <StepFechaCalendar
        dateOptions={dateOptions}
        date={date}
        onSelectDate={onSelectDate}
        availability={availability}
        slotStart={slotStart}
        onSelectStart={onSelectStart}
        onRetry={onRetry}
        slotNotice={slotNotice}
        courts={courts}
        courtNameOf={courtNameOf}
      />
    );
  }
  return (
    <>
      <h2 className="bday-title">Elegí fecha y horario</h2>
      <p className="bday-lead">Te asignamos una cancha libre del club automáticamente.</p>
      {slotNotice && <Alert variant="warning">{slotNotice}</Alert>}

      {dateOptions.length === 0 ? (
        <div className="rmob-empty">
          <RIcon name="cal" width={32} height={32} />
          <p>No hay fechas disponibles para cumpleaños por ahora.</p>
        </div>
      ) : (
        <>
          <div className="bday-dates" role="group" aria-label="Fechas disponibles">
            {dateOptions.slice(0, visible).map((opt) => (
              <button
                type="button"
                key={opt.date}
                className={`bday-date${date === opt.date ? " active" : ""}`}
                aria-pressed={date === opt.date}
                aria-label={formatLongDate(opt.date)}
                onClick={() => onSelectDate(opt.date)}
              >
                <small>{opt.weekday}</small>
                <strong>{opt.day}</strong>
                <small>{opt.month}</small>
              </button>
            ))}
          </div>
          {visible < dateOptions.length && (
            <button type="button" className="r-btn-soft bday-more" onClick={() => setVisible((n) => n + DATES_PAGE)}>
              Ver más fechas
            </button>
          )}
        </>
      )}

      {date && (
        <>
          <h3 className="bday-subtitle">Horarios del {formatLongDate(date)}</h3>
          {availability.status === "loading" && (
            <div className="rmob-loading">
              <Spinner animation="border" />
              Buscando horarios…
            </div>
          )}
          {availability.status === "error" && (
            <Alert variant="danger">
              {availability.error}{" "}
              <button type="button" className="r-btn-soft" onClick={onRetry}>
                Reintentar
              </button>
            </Alert>
          )}
          {availability.status === "ready" && availability.slots.length === 0 && (
            <div className="rmob-empty">
              <RIcon name="clock" width={32} height={32} />
              <p>No hay horarios para ese día. Probá con otra fecha.</p>
            </div>
          )}
          {availability.status === "ready" && availability.slots.length > 0 && (
            <div className="bday-starts">
              {availability.slots.map((slot) => {
                const selected = slot.slotStart === slotStart;
                const courtName = slot.available ? courtNameOf(slot.courtIds?.[0], courts) : "";
                return (
                  <button
                    type="button"
                    key={slot.slotStart}
                    disabled={!slot.available}
                    aria-pressed={selected}
                    className={`bday-start${selected ? " selected" : ""}`}
                    onClick={() => onSelectStart(slot.slotStart)}
                  >
                    <span className="bday-start-time">
                      {slot.slotStart} a {slot.slotEnd}
                    </span>
                    <span className="bday-start-court">{courtName}</span>
                    {!slot.available && <span className="bday-start-tag">No disponible</span>}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
}

/** Time slots of the chosen date as compact chips (desktop). */
const SlotButtons = ({ availability, slotStart, onSelectStart, courts, courtNameOf }) =>
  SLOT_PERIODS.map((period) => ({
    period,
    slots: availability.slots.filter((slot) => slotPeriod(slot.slotStart) === period),
  }))
    .filter((group) => group.slots.length > 0)
    .map(({ period, slots }) => (
      <div key={period} className="bday-slot-group">
        <h4 className="bday-slot-period">{period}</h4>
        <div className="bday-starts">
          {slots.map((slot) => {
            const selected = slot.slotStart === slotStart;
            const courtName = slot.available ? courtNameOf(slot.courtIds?.[0], courts) : "";
            return (
              <button
                type="button"
                key={slot.slotStart}
                disabled={!slot.available}
                aria-pressed={selected}
                className={`bday-start${selected ? " selected" : ""}`}
                onClick={() => onSelectStart(slot.slotStart)}
              >
                <span className="bday-start-time">
                  {slot.slotStart} a {slot.slotEnd}
                </span>
                <span className="bday-start-court">{courtName}</span>
                {!slot.available && <span className="bday-start-tag">No disponible</span>}
              </button>
            );
          })}
        </div>
      </div>
    ));

/** Desktop step 2: month calendar on the left, slots on the right, so the step needs no page scroll. */
function StepFechaCalendar({
  dateOptions,
  date,
  onSelectDate,
  availability,
  slotStart,
  onSelectStart,
  onRetry,
  slotNotice,
  courts,
  courtNameOf,
}) {
  const allowed = new Set(dateOptions.map((o) => o.date));
  const first = dateOptions[0] ? dayjs(dateOptions[0].date) : null;
  const last = dateOptions.length ? dayjs(dateOptions[dateOptions.length - 1].date) : null;
  return (
    <>
      <h2 className="bday-title">Elegí fecha y horario</h2>
      <p className="bday-lead">Te asignamos una cancha libre del club automáticamente.</p>
      {slotNotice && <Alert variant="warning">{slotNotice}</Alert>}

      {dateOptions.length === 0 ? (
        <div className="rmob-empty">
          <RIcon name="cal" width={32} height={32} />
          <p>No hay fechas disponibles para cumpleaños por ahora.</p>
        </div>
      ) : (
        <div className="bday-datetime">
          <div className="bday-calendar" aria-label="Fechas disponibles">
            <InlineDayPicker
              desktop
              value={date}
              onChange={onSelectDate}
              isBookable={(key) => allowed.has(key)}
              minDate={first}
              maxDate={last}
            />
          </div>
          <div className="bday-slots">
            <div className="bday-slots-scroll">
            {!date ? (
              <div className="rmob-empty">
                <RIcon name="clock" width={32} height={32} />
                <p>Elegí un día en el calendario para ver los horarios.</p>
              </div>
            ) : (
              <>
                <h3 className="bday-subtitle">Horarios del {formatLongDate(date)}</h3>
                {availability.status === "loading" && (
                  <div className="rmob-loading">
                    <Spinner animation="border" />
                    Buscando horarios…
                  </div>
                )}
                {availability.status === "error" && (
                  <Alert variant="danger">
                    {availability.error}{" "}
                    <button type="button" className="r-btn-soft" onClick={onRetry}>
                      Reintentar
                    </button>
                  </Alert>
                )}
                {availability.status === "ready" && availability.slots.length === 0 && (
                  <div className="rmob-empty">
                    <RIcon name="clock" width={32} height={32} />
                    <p>No hay horarios para ese día. Probá con otra fecha.</p>
                  </div>
                )}
                {availability.status === "ready" && availability.slots.length > 0 && (
                  <SlotButtons
                    availability={availability}
                    slotStart={slotStart}
                    onSelectStart={onSelectStart}
                    courts={courts}
                    courtNameOf={courtNameOf}
                  />
                )}
              </>
            )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const QUESTION_FIELDS = {
  birthdayPerson: { label: "¿Quién cumple?", placeholder: "Nombre del cumpleañero", type: "text" },
  age: { label: "¿Cuántos años cumple?", placeholder: "Ej. 8", type: "number" },
  guests: { label: "Cantidad de invitados", placeholder: "Ej. 15", type: "number" },
};

export function StepDetalles({
  pkg,
  mode,
  date,
  slotStart,
  courtName,
  amounts,
  depositPercent,
  config,
  enabled,
  form,
  onField,
  errors,
  policyAccepted,
  onPolicy,
  depositBlocked,
  submitError,
  hideSummary = false,
}) {
  const field = (key, label, input, hint) => (
    <div className="rmob-field" key={key}>
      <label htmlFor={`bday-${key}`}>{label}</label>
      {input}
      {hint && <span className="bday-field-hint">{hint}</span>}
      {errors[key] && <span className="rmob-field-error">{errors[key]}</span>}
    </div>
  );

  return (
    <>
      <h2 className="bday-title">Tus datos</h2>
      {!hideSummary && (
      <div className="bday-summary" aria-label="Resumen del cumpleaños">
        <div className="bday-row">
          <span>Paquete</span>
          <strong>{pkg.name}</strong>
        </div>
        <div className="bday-row">
          <span>Fecha</span>
          <strong>{formatLongDate(date)}</strong>
        </div>
        <div className="bday-row">
          <span>Horario</span>
          <strong>
            {slotStart} hs · {formatDuration(pkg.durationMinutes)}
          </strong>
        </div>
        {courtName && (
          <div className="bday-row">
            <span>Cancha</span>
            <strong>{courtName}</strong>
          </div>
        )}
        <div className="bday-row total">
          <span>Total</span>
          <strong>{formatMoney(amounts.total)}</strong>
        </div>
        {depositPercent > 0 && (
          <div className="bday-row">
            <span>Seña ({depositPercent}%)</span>
            <strong>{formatMoney(amounts.deposit)}</strong>
          </div>
        )}
        <div className="bday-row">
          <span>Saldo a pagar en el club</span>
          <strong>{formatMoney(amounts.balance)}</strong>
        </div>
      </div>
      )}

      {enabled.length > 0 && <h3 className="bday-subtitle">Sobre el cumple</h3>}
      {enabled.map((key) => {
        if (key === "comments") {
          return field(
            key,
            "Comentarios para el club",
            <div className="inp" style={{ height: "auto", padding: "12px 14px" }}>
              <textarea
                id="bday-comments"
                rows={3}
                value={form.comments}
                maxLength={500}
                placeholder="Alergias, decoración, cualquier detalle que quieras avisar"
                style={{ border: "none", outline: "none", width: "100%", resize: "none", fontFamily: "inherit", fontSize: 14 }}
                onChange={(e) => onField("comments", e.target.value)}
              />
            </div>
          );
        }
        const cfg = QUESTION_FIELDS[key];
        return field(
          key,
          cfg.label,
          <div className="inp">
            <input
              id={`bday-${key}`}
              type={cfg.type}
              min={cfg.type === "number" ? 1 : undefined}
              value={form[key]}
              placeholder={cfg.placeholder}
              onChange={(e) => onField(key, e.target.value)}
            />
          </div>
        );
      })}

      <h3 className="bday-subtitle">Quién reserva</h3>
      {field(
        "name",
        <>
          Nombre y apellido <span className="req">*</span>
        </>,
        <div className="inp">
          <RIcon name="user" />
          <input id="bday-name" value={form.name} placeholder="Ej. Camila Rodríguez" onChange={(e) => onField("name", e.target.value)} />
        </div>
      )}
      {field(
        "email",
        <>
          Email <span className="req">*</span>
        </>,
        <div className="inp">
          <RIcon name="mail" />
          <input id="bday-email" type="email" value={form.email} placeholder="tu@email.com" onChange={(e) => onField("email", e.target.value)} />
        </div>
      )}
      {field(
        "phone",
        <>
          WhatsApp / teléfono <span className="req">*</span>
        </>,
        <PhoneCountryInput
          countryCode={form.countryCode}
          number={form.phone}
          error={errors.phone}
          inputProps={{ id: "bday-phone" }}
          onChange={({ countryCode, number }) => {
            onField("countryCode", countryCode);
            onField("phone", number);
          }}
        />
      )}
      {field(
        "dni",
        <>
          DNI <span className="req">*</span>
        </>,
        <div className="inp">
          <RIcon name="user" />
          <input
            id="bday-dni"
            inputMode="numeric"
            value={form.dni}
            placeholder="Tu DNI"
            onChange={(e) => onField("dni", e.target.value.replace(/\D/g, "").slice(0, 10))}
          />
        </div>
      )}

      <h3 className="bday-subtitle">Política del club</h3>
      {config.policy?.text ? <div className="bday-policy">{config.policy.text}</div> : null}
      {config.policy?.refundDays > 0 && (
        <p className="bday-field-hint">La seña se devuelve si cancelás con {config.policy.refundDays} días de anticipación.</p>
      )}
      <label className="bday-consent">
        <input type="checkbox" checked={policyAccepted} onChange={(e) => onPolicy(e.target.checked)} />
        <span>Leí y acepto la política</span>
      </label>
      {errors.policy && <span className="rmob-field-error">{errors.policy}</span>}

      <p className="bday-note">
        {mode === "approval"
          ? `Mandás una solicitud y el club te responde en hasta ${config.responseHours} h. Pagás la seña solo si la acepta.`
          : depositPercent > 0
            ? "Pagás la seña online y el cumple queda confirmado al instante."
            : "El cumple queda confirmado al instante, sin pagar nada online."}
      </p>
      {depositBlocked && (
        <p className="bday-note warn" role="alert">
          Este club todavía no está tomando cumpleaños con seña online.
        </p>
      )}
      {submitError && <Alert variant="danger">{submitError}</Alert>}
    </>
  );
}

const ResultShell = ({ icon = "check", tone = "", title, children }) => (
  <div className="bday-state">
    <div className={`bday-badge ${tone}`}>
      <RIcon name={icon} width={34} height={34} />
    </div>
    <h1>{title}</h1>
    {children}
  </div>
);

export function ResultEnviada({ email, respondBy, requestToken, onViewRequest, onBack }) {
  return (
    <ResultShell title="Solicitud enviada">
      {respondBy && (
        <p>
          El club te responde antes del <strong>{formatDeadline(respondBy)}</strong>.
        </p>
      )}
      <p>
        Todavía no pagaste nada y el horario no está reservado. Si el club acepta, te mandamos a <strong>{email}</strong> el
        link para pagar la seña.
      </p>
      <div className="bday-state-actions">
        {requestToken && (
          <button type="button" className="r-btn-primary" onClick={onViewRequest}>
            Ver el estado de mi solicitud
          </button>
        )}
        <button type="button" className="r-btn-soft" onClick={onBack}>
          Volver al club
        </button>
      </div>
    </ResultShell>
  );
}

export function ResultConfirmada({ result, email, pkg, date, slotStart, onBack }) {
  return (
    <ResultShell title="¡Listo, el cumple está confirmado!">
      <p>Te mandamos el comprobante a {email}.</p>
      <div className="bday-summary">
        {result?.reservationCode && (
          <div className="bday-row">
            <span>Código</span>
            <strong>{result.reservationCode}</strong>
          </div>
        )}
        <div className="bday-row">
          <span>Paquete</span>
          <strong>{pkg?.name}</strong>
        </div>
        <div className="bday-row">
          <span>Fecha</span>
          <strong>
            {formatLongDate(date)}, {slotStart} hs
          </strong>
        </div>
        <div className="bday-row total">
          <span>A pagar en el club</span>
          <strong>{formatMoney(result?.balanceDue ?? result?.totalPrice)}</strong>
        </div>
      </div>
      <div className="bday-state-actions">
        <button type="button" className="r-btn-soft" onClick={onBack}>
          Volver al club
        </button>
      </div>
    </ResultShell>
  );
}

export { ResultShell };
